import { useState, useRef, useCallback } from "react";

function Frame({ src, alt, className, priority }) {
  return (
    <picture>
      <source srcSet={`${src}.webp`} type="image/webp" />
      <img
        src={`${src}.jpg`}
        alt={alt}
        className={className}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        draggable="false"
      />
    </picture>
  );
}

/**
 * Draggable before/after comparison slider. Keyboard accessible via range input.
 */
export default function BeforeAfterSlider({ project, priority = false }) {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);

  const setFromClientX = useCallback((clientX) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  }, []);

  const onPointerDown = (e) => {
    setFromClientX(e.clientX);
    const move = (ev) => setFromClientX(ev.clientX);
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  return (
    <figure className="m-0">
      <div
        ref={ref}
        className="relative overflow-hidden bg-ink select-none border border-gold/20"
        style={{ aspectRatio: project.aspect || "3 / 4", touchAction: "none" }}
        onPointerDown={onPointerDown}
        data-testid={`ba-slider-${project.id}`}
      >
        {/* After (full) */}
        <Frame
          src={project.after}
          alt={`After — ${project.title} in Greensboro NC`}
          priority={priority}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <span className="absolute top-3 right-3 z-10 eyebrow !tracking-[0.15em] bg-ink/70 text-white px-2.5 py-1">
          After
        </span>

        {/* Before (clipped) */}
        <div
          className="absolute inset-0 overflow-hidden"
          style={{ width: `${pos}%` }}
          aria-hidden="true"
        >
          <div className="h-full" style={{ width: ref.current ? ref.current.clientWidth : "100%" }}>
            <Frame
              src={project.before}
              alt=""
              priority={priority}
              className="absolute inset-0 h-full w-full object-cover"
            />
          </div>
          <span className="absolute top-3 left-3 eyebrow !tracking-[0.15em] bg-white/85 text-ink px-2.5 py-1">
            Before
          </span>
        </div>

        {/* Handle */}
        <div
          className="absolute top-0 bottom-0 z-10 w-px bg-white/90 pointer-events-none"
          style={{ left: `${pos}%` }}
          aria-hidden="true"
        >
          <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white text-ink flex items-center justify-center shadow-md">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M9 6 L4 12 L9 18 M15 6 L20 12 L15 18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </div>

        {/* Accessible control */}
        <input
          type="range"
          min="0"
          max="100"
          value={pos}
          onChange={(e) => setPos(Number(e.target.value))}
          aria-label={`Reveal before and after for ${project.title}`}
          className="absolute inset-0 z-20 h-full w-full cursor-ew-resize opacity-0"
          data-testid={`ba-range-${project.id}`}
        />
      </div>
      <figcaption className="mt-3 flex items-baseline justify-between gap-3">
        <span className="font-serif text-xl text-ink">{project.title}</span>
        {project.label && <span className="eyebrow text-rose">{project.label}</span>}
      </figcaption>
    </figure>
  );
}
